// src/lib/errorReporter.js
// Captura global de errores frontend (window.onerror + unhandledrejection)
// y los persiste en `app_errors` para el tab Sistema del superadmin.
//
// Estrategia:
// - Rate limit local: max 10 errores por minuto por sesión (evita drenar
//   BD si un componente entra en loop).
// - Dedup hash por mensaje+stack+url: dentro de 1 minuto el mismo error
//   no se reporta dos veces.
// - Best-effort: si el INSERT falla, lo loguea en consola y sigue.
// - Solo reporta si hay sesión activa (auth.uid). Errores anónimos
//   (chat público) no se reportan para no exponer la tabla.
// - **ChunkLoadError**: NO se reporta como error (es artefacto natural de
//   deploys con code-splitting cuando un usuario tenía la app abierta y
//   los chunks viejos ya no existen). Se muestra un banner pidiendo
//   refrescar la página.
// - **Network errors transitorios**: se reportan como warning, no error,
//   con dedup más agresivo (5 min) — son típicamente WiFi caídas
//   momentáneas que se autocorrigen.

import { supabase } from './supabase';

const VENTANA_MS    = 60_000;   // 1 min
const MAX_POR_VENTANA = 10;
const recientes = []; // [{ at, hash }]

// Auditoría 15 may — A2: sanitizar tokens en la URL antes de persistirla en
// app_errors. Cualquier query param que parezca un token (t, token, code,
// access_token, refresh_token) se reemplaza por [REDACTED] para no leakear
// magic links / envio tokens / OAuth codes en logs.
const _PARAMS_SENSIBLES = new Set(['t', 'token', 'code', 'access_token', 'refresh_token', 'magic_link', 'key']);
function sanitizarUrl(rawUrl) {
  if (!rawUrl) return rawUrl;
  try {
    const u = new URL(rawUrl, typeof window !== 'undefined' ? window.location.origin : 'https://ia-vera.com');
    let mutado = false;
    for (const k of Array.from(u.searchParams.keys())) {
      if (_PARAMS_SENSIBLES.has(k.toLowerCase())) {
        u.searchParams.set(k, '[REDACTED]'); mutado = true;
      }
    }
    return mutado ? u.toString() : rawUrl;
  } catch (_) { return rawUrl; }
}

function hash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return h.toString(36);
}

async function reportar(payload) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return; // solo usuarios logeados

    const ahora = Date.now();
    // Limpiar fuera de la ventana
    while (recientes.length && ahora - recientes[0].at > VENTANA_MS) recientes.shift();
    if (recientes.length >= MAX_POR_VENTANA) return;

    const h = hash((payload.mensaje || '') + '|' + (payload.stack || '').slice(0, 200) + '|' + (payload.url || ''));
    if (recientes.find(r => r.hash === h && ahora - r.at < VENTANA_MS)) return;
    recientes.push({ at: ahora, hash: h });

    // Resolver inmobiliaria del user (best-effort) desde sessionStorage cache
    let inmobiliaria_id = null;
    try {
      inmobiliaria_id = JSON.parse(sessionStorage.getItem('vera_inm_id') || 'null');
    } catch (_) {}

    const { error } = await supabase.from('app_errors').insert({
      fuente:    'frontend',
      severidad: payload.severidad || 'error',
      mensaje:   (payload.mensaje || '').slice(0, 1000),
      stack:     (payload.stack   || '').slice(0, 4000),
      url:       sanitizarUrl(payload.url || (typeof window !== 'undefined' ? window.location.href : null)),
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : null,
      user_id:    user.id,
      inmobiliaria_id,
      contexto:   payload.contexto || {},
    });
    if (error) console.warn('[errorReporter] insert fail:', error.message);
  } catch (e) {
    console.warn('[errorReporter]', e?.message || e);
  }
}

// Detecta si un error es un ChunkLoadError (chunk JS o CSS no encontrado
// porque hubo un deploy y el bundle viejo del browser intenta cargar un
// chunk que ya no existe en el server).
function esChunkLoadError(msg) {
  if (!msg) return false;
  return /Loading chunk \d+ failed/i.test(msg)
      || /Loading CSS chunk/i.test(msg)
      || /ChunkLoadError/i.test(msg);
}

// Detecta errores de red transitorios (WiFi cae un segundo, servidor
// devuelve TimeoutError, etc). Estos se autocorrigen y no son bugs.
function esNetworkErrorTransitorio(msg) {
  if (!msg) return false;
  return /Failed to fetch/i.test(msg)
      || /NetworkError/i.test(msg)
      || /Network error: fetch failed/i.test(msg)
      || /Load failed/i.test(msg);   // Safari
}

// Banner discreto que aparece cuando hay versión nueva. Se monta una
// sola vez por sesión y se queda hasta que el usuario haga refresh.
function mostrarBannerVersionNueva() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('vera-version-banner')) return; // ya mostrado

  const banner = document.createElement('div');
  banner.id = 'vera-version-banner';
  banner.setAttribute('role', 'alert');
  banner.style.cssText = [
    'position:fixed', 'left:50%', 'top:16px', 'transform:translateX(-50%)',
    'z-index:99999',
    'background:#FF6428', 'color:#fff', 'padding:11px 18px',
    'border-radius:99px', 'box-shadow:0 6px 20px rgba(0,0,0,0.18)',
    'font-family:system-ui,-apple-system,sans-serif', 'font-size:13.5px',
    'font-weight:600', 'display:flex', 'gap:12px', 'align-items:center',
    'max-width:90vw',
  ].join(';');
  banner.innerHTML = `
    <span>🚀 Hay una versión nueva de Vera</span>
    <button id="vera-version-refresh" style="
      background:#1A1814;color:#fff;border:0;padding:6px 14px;
      border-radius:99px;font-weight:700;font-size:12.5px;
      cursor:pointer;font-family:inherit;
    ">Refrescar</button>
  `;
  document.body.appendChild(banner);
  document.getElementById('vera-version-refresh')?.addEventListener('click', () => {
    window.location.reload();
  });
}

export function instalarErrorReporter() {
  if (typeof window === 'undefined') return;
  if (window.__veraErrorReporterInstalled) return;
  window.__veraErrorReporterInstalled = true;

  window.addEventListener('error', (event) => {
    // Filtrar errores de scripts de terceros sin información (CORS opaco).
    if (!event.message && !event.error) return;

    const msg = event.message || event.error?.message || '';

    // ChunkLoadError → no reportar como error, mostrar banner de versión nueva.
    if (esChunkLoadError(msg)) {
      mostrarBannerVersionNueva();
      return;
    }

    // Network transitorio → bajar severidad a warning.
    const sev = esNetworkErrorTransitorio(msg) ? 'warning' : 'error';

    reportar({
      severidad: sev,
      mensaje:   msg || 'Error sin mensaje',
      stack:     event.error?.stack,
      url:       sanitizarUrl(event.filename || window.location.href),
      contexto: {
        line:   event.lineno,
        column: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const msg = reason?.message || String(reason || 'Promise rechazada sin razón');

    // ChunkLoadError → no reportar, mostrar banner.
    if (esChunkLoadError(msg)) {
      mostrarBannerVersionNueva();
      return;
    }

    const sev = esNetworkErrorTransitorio(msg) ? 'warning' : 'error';

    reportar({
      severidad: sev,
      mensaje:   msg,
      stack:     reason?.stack,
      url:       sanitizarUrl(window.location.href),
      contexto:  { tipo: 'unhandled_rejection' },
    });
  });
}

// Para reportar errores manuales desde un try/catch que quieres trackear.
export function reportarError(error, contexto = {}) {
  if (!error) return;
  reportar({
    mensaje:  error.message || String(error),
    stack:    error.stack,
    contexto,
  });
}
